import {
  type DetailedOrganizationRes,
  type PublicOpenSearchResponsePagedData,
  rdbV1CoachesSlimProfileDetail,
  rdbV1FiltersYearsList,
  rdbV1OrganizationsSlimProfileDetail,
  type SlimCoachResponse,
  type SlimOrganizationRes,
  type StateResponse,
  v1PeopleSearchList,
  type V1PeopleSearchListParams,
  v1StatesList,
} from '@on3/api';
import type {
  ErrorData,
  IGenericPageProps,
  IUser,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import {
  AdvancedSearch,
  SearchKeys,
} from '@public/components/Db/Search/AdvancedSearch';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import type { AxiosError } from 'axios';
import type { GetServerSideProps } from 'next';

export interface IPlayerSearchPage {
  searchData: PublicOpenSearchResponsePagedData;
  yearsData: number[];
  statesData: StateResponse[];
  relatedModel?: {
    highSchoolData?: DetailedOrganizationRes;
    committedOrganizationData?: DetailedOrganizationRes;
    recruitedByPersonData?: SlimCoachResponse;
    officialVisitData?: DetailedOrganizationRes;
    attendedGraduatedData?: DetailedOrganizationRes;
    recruitmentInterestData?: DetailedOrganizationRes;
    distanceFromOrgData?: DetailedOrganizationRes;
  } | null;
  userData?: IUser | null;
  error?: ErrorData;
  isLoading?: boolean;
}

export const SearchPeopleRoute = '/rdb/v1/people/search';

export const defaultPersonSearchData: PublicOpenSearchResponsePagedData = {
  list: [],
  pagination: {
    count: 0,
    currentPage: 0,
    itemsPerPage: 50,
    limit: 50,
    offset: 0,
    pageCount: 0,
  },
};

interface ISearchPage extends IPlayerSearchPage, IGenericPageProps {}

export const SearchPage = ({
  searchData,
  yearsData,
  statesData,
  userData,
  relatedModel,
  error,
  ...props
}: ISearchPage) => {
  const title = 'Athlete Search - Find Top Sports Talent | On3.com';
  const description = `Utilize On3.com's athlete search to explore extensive profiles of aspiring and established athletes. Find detailed information on sports talent across various disciplines and levels.`;
  const canonical = `https://www.on3.com/rivals/search/`;

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      title={title}
    >
      <AdvancedSearch
        relatedModel={relatedModel}
        searchData={searchData}
        statesData={statesData}
        yearsData={yearsData}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<IPlayerSearchPage> = async (
  ctx,
) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const officialVisitStartDate = ctx.query.officialVisitStartDate
    ? new Date(`${ctx.query.officialVisitStartDate}`).getTime() / 1000
    : undefined;
  const officialVisitEndDate = ctx.query.officialVisitEndDate
    ? new Date(`${ctx.query.officialVisitEndDate}`).getTime() / 1000
    : undefined;

  try {
    const params = {
      ...ctx.query,
      officialVisitStartDate,
      officialVisitEndDate,
      limit: 25,
    };

    const keysToFetch: {
      key: keyof V1PeopleSearchListParams | SearchKeys.DISTANCE_FROM_ORG_KEY;
      alias: string;
    }[] = [
      { key: SearchKeys.HIGH_SCHOOL_ORG_KEYS, alias: 'highSchoolData' },
      {
        key: SearchKeys.COMMITTED_ORGANIZATION_KEY,
        alias: 'committedOrganizationData',
      },
      {
        key: SearchKeys.RECRUITED_BY_PERSON_KEY,
        alias: 'recruitedByPersonData',
      },
      { key: SearchKeys.OFFICIAL_VISIT_ORG_KEYS, alias: 'officialVisitData' },
      {
        key: SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY,
        alias: 'attendedGraduatedData',
      },
      {
        key: SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS,
        alias: 'recruitmentInterestData',
      },
      {
        key: SearchKeys.DISTANCE_FROM_ORG_KEY,
        alias: 'distanceFromOrgData',
      },
    ];

    const data = await v1PeopleSearchList(proxyApi, params);
    const yearsData = await rdbV1FiltersYearsList(proxyApi);
    const statesData = await v1StatesList(proxyApi, { countryKey: 1 });

    const relatedModel: {
      [key: string]: (SlimOrganizationRes | SlimCoachResponse)[] | null;
    } = {};

    for (const { key, alias } of keysToFetch) {
      const queryValue = ctx.query[key];

      if (queryValue) {
        try {
          let details;

          if (key === SearchKeys.RECRUITED_BY_PERSON_KEY) {
            // Handle recruitedByPersonKey (single value)
            details = [
              await rdbV1CoachesSlimProfileDetail(proxyApi, {
                personKey: +queryValue,
              }),
            ];
          } else if (Array.isArray(queryValue)) {
            // Handle array of organization keys
            details = await Promise.all(
              queryValue.map((value) =>
                rdbV1OrganizationsSlimProfileDetail(proxyApi, +value),
              ),
            );
          } else {
            // Handle single organization key
            details = [
              await rdbV1OrganizationsSlimProfileDetail(proxyApi, +queryValue),
            ];
          }

          relatedModel[alias] = details;
        } catch (error) {
          console.error(`Error fetching data for ${key}:`, error);
          relatedModel[alias] = null;
        }
      } else {
        relatedModel[alias] = null;
      }
    }

    const isBaseSearch = Object.keys(ctx.query).length === 0;
    const searchData = isBaseSearch ? defaultPersonSearchData : data;

    return {
      props: {
        pageType: 'database',
        searchData,
        yearsData,
        statesData,
        siteData: siteData || [],
        userData,
        relatedModel,
      },
    };
  } catch (e: unknown) {
    const { response }: AxiosError<ErrorData> = e as AxiosError<ErrorData>;

    console.error('DB Search Error: ', response?.data);

    return {
      props: {
        pageType: 'database',
        error: response?.data,
        searchData: defaultPersonSearchData,
        yearsData: [],
        statesData: [],
        siteData: siteData || [],
        userData,
        relatedModel: {},
      },
    };
  }
};

export default withValidUrl(SearchPage);
